import { Component } from '../Component';
import { ArgSpecDictionary } from '../ComponentUtils';

import countries from './countries.html?raw';
import states from './states.html?raw';

const DEBUG_VERBOSE: boolean = false;
export const HHDSSalesforceTagName: string = 'hhds-salesforce';
const TAG_NAME: string = HHDSSalesforceTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;

export const HHDSSalesforceAttrNames = {
	DefaultCountryText: 'default-country-text',
};

const Attrs = HHDSSalesforceAttrNames;

const InvalidCSSClassName = 'invalid';

// ////////////////////////////////////////////////////////////////////

declare global {
	interface Window {
		captchaOnLoad: () => void;
		loadCaptcha: any;
	}
}

declare var grecaptcha: any;

export class HHDSSalesforce extends Component {
	private inputStateEl!: HTMLInputElement;
	private inputCountryEl!: HTMLSelectElement;
	private formEl!: HTMLFormElement;
	private inputPhoneEl!: HTMLInputElement;
	private inputPropertyEl!: HTMLInputElement;
	private checkboxContainerEl!: HTMLElement;
	private checkboxInputEl!: HTMLInputElement;
	private btnSubmitEl!: HTMLButtonElement;

	private isProduction: boolean = false;

	private endpoint!: string;
	private captcha!: string;
	private requiredInputs!: NodeListOf<HTMLInputElement | HTMLSelectElement>;

	constructor() {
		super();
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.init.name);

		const html: string = /* html */ `
		<div class="${TAG_NAME}">
      <div class="container">
				<div class="grid">
					<div class="col-span-4 md:col-span-8 md:col-start-3 lg:col-start-7 lg:col-span-12">
					<slot></slot>
					</div>
				</div>
			</div>
		</div>
    `;

		this.setInnerHtml('', '', html);

		this.observeSlotChanges(true);
	}

	private setupFormFields(): void {
		this.setEnvironment();

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
		// Create refs

		this.formEl = this.querySelector('form') as HTMLFormElement;

		if (!this.formEl) throw new Error('Unable to derive form!');

		this.inputStateEl = document.getElementById('00Ni000000FlJ6F') as HTMLInputElement;

		if (!this.inputStateEl) return console.warn('Unable to derive selector!');

		this.inputCountryEl = document.getElementById('00Ni000000FlJ6K') as HTMLSelectElement;

		if (!this.inputCountryEl) return console.warn('Unable to derive selector!');

		this.inputPhoneEl = document.getElementById('phone') as HTMLInputElement;

		if (!this.inputPhoneEl) return console.warn('Unable to derive selector!');

		this.checkboxContainerEl = this.querySelector('.custom-checkbox') as HTMLElement;

		if (!this.checkboxContainerEl) return console.warn('Unable to derive selector!');

		this.checkboxInputEl = document.getElementById('00Ni000000HmD2I') as HTMLInputElement;

		if (!this.checkboxInputEl) return console.warn('Unable to derive selector!');

		this.inputPropertyEl = document.getElementById('00Ni000000AgbA0') as HTMLInputElement;

		if (!this.inputPropertyEl) return console.warn('Unable to derive selector!');

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

		this.inputCountryEl.innerHTML = this.inputCountryEl.innerHTML + countries;
		this.inputStateEl.innerHTML = this.inputStateEl.innerHTML + states;

		this.btnSubmitEl = this.querySelector('button[type="submit"]') as HTMLButtonElement;

		this.setupPhoneInput();

		this.setupFormSubmit();

		this.setupCountryInput();

		this.setupPropertyInput();

		if (this.isProduction) {
			this.configureForProduction();
		} else {
			this.configureForStaging();
		}

		this.loadReCaptchaScript();

		// Run intially to hide state field
		this.onCountryChange();
		// this.filterRegions();

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

		this.requiredInputs = this.formEl.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input:required, select:required');

		this.requiredInputs.forEach((input) => {
			input.addEventListener('input', () => {
				if (input.checkValidity()) {
					input.classList.remove(InvalidCSSClassName);
				} else {
					input.classList.add(InvalidCSSClassName);
				}
			});
		});
	}

	// ////////////////////////////////////////////////////////////////////

	setEnvironment() {
		const { productionUrl } = this.dataset;

		if (!productionUrl) return console.warn('No productionUrl set!');

		const urls = productionUrl.split(',');
		const url = window.location.hostname;

		this.isProduction = urls.indexOf(url) !== -1;

		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.setEnvironment.name, `this.isProduction`, this.isProduction);
	}

	// ////////////////////////////////////////////////////////////////////

	loadReCaptchaScript() {
		window.captchaOnLoad = this.onReCaptchaScriptLoaded;

		const url = 'https://www.google.com/recaptcha/api.js';
		const queryString = '?onload=captchaOnLoad&render=explicit';
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.src = url + queryString;
		script.async = true;
		script.defer = true;

		document.body.appendChild(script);
	}

	onReCaptchaScriptLoaded = () => {
		grecaptcha.render('captcha_container', {
			sitekey: this.captcha,
			callback: (response: any) => {
				this.btnSubmitEl.disabled = false;
				DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.onReCaptchaScriptLoaded.name, { response });
			},
		});
	};

	// ////////////////////////////////////////////////////////////////////

	configureForProduction() {
		const { productionEndpoint, productionCaptcha } = this.dataset;

		if (!productionEndpoint || !productionCaptcha) {
			throw new Error('Unable to determine productionEndpoint or productionCaptcha');
		}

		this.endpoint = productionEndpoint.split('').reverse().join('');
		this.captcha = productionCaptcha;

		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.configureForProduction.name, `this.endpoint`, this.endpoint, `this.captcha`, this.captcha);
	}

	// ////////////////////////////////////////////////////////////////////

	configureForStaging() {
		const { stagingEndpoint, stagingCaptcha } = this.dataset;

		if (!stagingEndpoint || !stagingCaptcha) {
			throw new Error('Unable to determine stagingEndpoint or stagingCaptcha');
		}

		this.endpoint = stagingEndpoint.split('').reverse().join('');
		this.captcha = stagingCaptcha;

		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.configureForStaging.name, `this.endpoint`, this.endpoint, `this.captcha`, this.captcha);
	}

	// ////////////////////////////////////////////////////////////////////

	setupCountryInput() {
		this.inputCountryEl.addEventListener('change', () => this.onCountryChange());
	}

	// ////////////////////////////////////////////////////////////////////

	setupFormSubmit() {
		this.formEl.addEventListener('submit', this.onSubmit);
	}

	// ////////////////////////////////////////////////////////////////////

	setupPhoneInput() {
		this.inputPhoneEl.addEventListener('input', this.onInputPhone);
	}

	// ////////////////////////////////////////////////////////////////////

	setupPropertyInput() {
		this.inputPropertyEl.addEventListener('change', () => this.onPropertyChange());
	}

	// ////////////////////////////////////////////////////////////////////

	onPropertyChange() {
		let redirectUrlElement = document.getElementById('retUrl') as HTMLInputElement;
		if (redirectUrlElement) {
			const redirectUrl = this.dataset.redirectUrl || '';
			redirectUrlElement.setAttribute(
				'value',
				redirectUrl + '?property_interest=' + this.inputPropertyEl.value.toLowerCase().replace(/\s+/g, '-')
			);
		}
	}

	// ////////////////////////////////////////////////////////////////////

	onSubmit = (event: SubmitEvent) => {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.onSubmit.name);

		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.onSubmit.name);

		// Test each item for validity, triggers the CSS changes
		this.requiredInputs.forEach((input) => {
			if (input.checkValidity()) {
				input.classList.remove(InvalidCSSClassName);
			} else {
				input.classList.add(InvalidCSSClassName);
			}
		});

		if (!this.formEl.checkValidity()) {
			event.preventDefault();
		} else {
			event.preventDefault();
			this.btnSubmitEl.disabled = true;
			this.btnSubmitEl.innerHTML = 'Submitting...';
			this.formEl.action = this.endpoint;
			this.formEl.submit();
		}
	};

	// ////////////////////////////////////////////////////////////////////

	onCountryChange = () => {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.onCountryChange.name);

		const stateEl = this.querySelector('#sf-form-state') as HTMLElement;

		if (this.inputCountryEl.value !== 'US') {
			this.inputStateEl.value = '';
			this.inputStateEl.required = false;
			stateEl.style.display = 'none';
		} else {
			this.inputStateEl.required = true;
			stateEl.removeAttribute('style');
		}

		this.filterRegions();

		requestAnimationFrame(() => {
			// Update required inputs as there has just been one added or removed
			this.requiredInputs = this.formEl.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input:required, select:required');
		});
	};

	// ////////////////////////////////////////////////////////////////////

	filterRegions() {
		// Not sure why this is removing some countries.
		setTimeout(() => {
			const countrySelectEl = document.getElementById('00Ni000000FlJ6K') as HTMLSelectElement;

			for (var i = 0; i < countrySelectEl.length; i++) {
				// DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.filterRegions.name, countrySelectEl.options[i].value);

				if (
					countrySelectEl.options[i].value == 'MH' ||
					countrySelectEl.options[i].value == 'PW' ||
					countrySelectEl.options[i].value == 'FM'
				) {
					countrySelectEl.remove(i);
				}
			}
		}, 500);
	}

	// ////////////////////////////////////////////////////////////////////

	onInputPhone = (e: Event) => {
		const input = e.target as HTMLInputElement;

		let value = input.value.replace(/\D/g, ''); // Remove all non-numeric characters

		// Format as (XXX-XXX-XXXX)
		if (value.length > 3 && value.length <= 6) {
			value = `${value.slice(0, 3)}-${value.slice(3)}`;
		} else if (value.length > 6) {
			value = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6, 10)}`;
		}

		input.value = value; // Update input value
	};

	// ////////////////////////////////////////////////////////////////////

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: HTMLElement[]): void {
		logger.log(LOG_PREFIX, `onSlotChange`);
		this.setupFormFields();
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.DefaultCountryText]: {
		description: 'The messaging shown in the country dropdown.',
		defaultValue: '',
		type: String,
	},
};
